import type { Prisma } from '@dealstack/prisma-client';

export enum DealEventCategory {
	CAPITAL = 'CAPITAL',
	PARTICIPANT = 'PARTICIPANT',
	ADMIN = 'ADMIN',
	MESSAGE = 'MESSAGE',
	ACCESS = 'ACCESS',
	AUTH = 'AUTH',
}

export enum DealEventSource {
	AUDIT = 'AUDIT',
	LEDGER = 'LEDGER',
	INVESTMENT_WORKFLOW = 'INVESTMENT_WORKFLOW',
	KYC = 'KYC',
	DOCUMENT_WORKFLOW = 'DOCUMENT_WORKFLOW',
	BANK_ACCOUNT_VERIFICATION = 'BANK_ACCOUNT_VERIFICATION',
	BANK_ACCOUNT_STATE = 'BANK_ACCOUNT_STATE',
	ACCESS = 'ACCESS',
	AUTH = 'AUTH',
	EMAIL_SENT = 'EMAIL_SENT',
}

export enum DealEventStatus {
	COMPLETED = 'COMPLETED',
	IN_PROGRESS = 'IN_PROGRESS',
	PENDING = 'PENDING',
	APPROVED = 'APPROVED',
	DRAFT = 'DRAFT',
	FAILED = 'FAILED',
	CANCELLED = 'CANCELLED',
	SUCCESS = 'SUCCESS',
	SENT = 'SENT',
	DELIVERED = 'DELIVERED',
	OPENED = 'OPENED',
	READ = 'READ',
	BOUNCED = 'BOUNCED',
	WARNING = 'WARNING',
	EXPIRED = 'EXPIRED',
	REJECTED = 'REJECTED',
}

export const EMAIL_EVENT_TYPE_PREFIX = 'EMAIL';

/**
 * Statuses each category's arms can actually report, in enum order. Offering anything else gives
 * a filter that always returns nothing; omitting one hides rows that exist. The backend owns the
 * per-source maps this is derived from, and its spec fails if the two drift.
 */
export const DEAL_EVENT_STATUSES_BY_CATEGORY: Record<DealEventCategory, DealEventStatus[]> = {
	[DealEventCategory.CAPITAL]: [DealEventStatus.COMPLETED],
	[DealEventCategory.ADMIN]: [DealEventStatus.COMPLETED],
	[DealEventCategory.PARTICIPANT]: [
		DealEventStatus.COMPLETED,
		DealEventStatus.IN_PROGRESS,
		DealEventStatus.PENDING,
		DealEventStatus.APPROVED,
		DealEventStatus.DRAFT,
		DealEventStatus.FAILED,
		DealEventStatus.CANCELLED,
		DealEventStatus.SUCCESS,
		DealEventStatus.WARNING,
		DealEventStatus.EXPIRED,
		DealEventStatus.REJECTED,
	],
	[DealEventCategory.ACCESS]: [
		DealEventStatus.COMPLETED,
		DealEventStatus.PENDING,
		DealEventStatus.FAILED,
		DealEventStatus.SUCCESS,
		DealEventStatus.WARNING,
	],
	[DealEventCategory.AUTH]: [
		DealEventStatus.PENDING,
		DealEventStatus.FAILED,
		DealEventStatus.SUCCESS,
		DealEventStatus.WARNING,
	],
	[DealEventCategory.MESSAGE]: [
		DealEventStatus.PENDING,
		DealEventStatus.FAILED,
		DealEventStatus.SENT,
		DealEventStatus.DELIVERED,
		DealEventStatus.OPENED,
		DealEventStatus.READ,
		DealEventStatus.BOUNCED,
		DealEventStatus.WARNING,
	],
};

/** Actor filter value for "no person did this"; the backend maps it to the system user and to null actors. */
export const SYSTEM_ACTOR_FILTER = 'SYSTEM';

/** Cap on the recipient filter's options; the DTO enforces it and the UI asks for it. */
export const MAX_RECIPIENT_OPTIONS = 100;

/** Cap on a keyword filter; the DTO rejects anything longer, so the input stops there too. */
export const MAX_SEARCH_LENGTH = 200;

/**
 * The audited tables the feed can report. The only sanctioned way to name a table outside the
 * backend: the frontend cannot import the Prisma client, and the generated client types
 * `entity.type` as a plain string.
 *
 * `satisfies` gates every entry against the real model names, so a rename or a typo fails the
 * build here, where the names are declared and referenced from. The import is type-only, so
 * nothing of the Prisma client reaches the browser bundle.
 */
export const AUDITED_TABLE = {
	Transaction: 'Transaction',
	Security: 'Security',
	ShareAssociation: 'ShareAssociation',
	Loan: 'Loan',
	HoldingEvent: 'HoldingEvent',
	InvestmentWorkflow: 'InvestmentWorkflow',
	Person: 'Person',
	PersonScope: 'PersonScope',
	PersonPerson: 'PersonPerson',
	PersonAddress: 'PersonAddress',
	Address: 'Address',
	BankAccount: 'BankAccount',
	TaxResidence: 'TaxResidence',
	EmploymentRecord: 'EmploymentRecord',
} as const satisfies Record<string, Prisma.ModelName>;

export const isAuditedTable = (table: string): boolean => Object.hasOwn(AUDITED_TABLE, table);

export const DERIVED_AUDIT_FIELDS: Readonly<Record<string, readonly string[]>> = {
	[AUDITED_TABLE.HoldingEvent]: ['description'],
};

/**
 * Which category an audited table's events fall under. Shared because the UI builds its type
 * filter from it, and a second copy there would drift the moment a table changes hands.
 */
export const DEAL_EVENT_TABLE_CATEGORY: Readonly<Record<string, DealEventCategory>> = {
	[AUDITED_TABLE.Transaction]: DealEventCategory.CAPITAL,
	[AUDITED_TABLE.Security]: DealEventCategory.CAPITAL,
	[AUDITED_TABLE.HoldingEvent]: DealEventCategory.CAPITAL,
	[AUDITED_TABLE.InvestmentWorkflow]: DealEventCategory.PARTICIPANT,
	[AUDITED_TABLE.ShareAssociation]: DealEventCategory.PARTICIPANT,
	[AUDITED_TABLE.Loan]: DealEventCategory.PARTICIPANT,
	[AUDITED_TABLE.Person]: DealEventCategory.ADMIN,
	[AUDITED_TABLE.PersonScope]: DealEventCategory.ADMIN,
	[AUDITED_TABLE.PersonPerson]: DealEventCategory.ADMIN,
	[AUDITED_TABLE.PersonAddress]: DealEventCategory.ADMIN,
	[AUDITED_TABLE.Address]: DealEventCategory.ADMIN,
	[AUDITED_TABLE.BankAccount]: DealEventCategory.ADMIN,
	[AUDITED_TABLE.TaxResidence]: DealEventCategory.ADMIN,
	[AUDITED_TABLE.EmploymentRecord]: DealEventCategory.ADMIN,
};

/** The payload field naming the person a directory row belongs to, as the audit trigger reads it. */
export const DIRECTORY_PERSON_FIELD: Readonly<Record<string, string>> = {
	[AUDITED_TABLE.PersonScope]: 'personId',
	[AUDITED_TABLE.PersonAddress]: 'personId',
	[AUDITED_TABLE.BankAccount]: 'personId',
	[AUDITED_TABLE.TaxResidence]: 'personId',
	[AUDITED_TABLE.PersonPerson]: 'sourcePersonId',
	[AUDITED_TABLE.EmploymentRecord]: 'employeeId',
};

/** The second person a directory row ties to its owner. */
export const DIRECTORY_RELATED_PERSON_FIELD: Readonly<Record<string, string>> = {
	[AUDITED_TABLE.PersonPerson]: 'targetPersonId',
	[AUDITED_TABLE.EmploymentRecord]: 'employerId',
};

export const tablesInCategory = (category: DealEventCategory): string[] =>
	Object.entries(DEAL_EVENT_TABLE_CATEGORY)
		.filter(([, tableCategory]) => tableCategory === category)
		.map(([table]) => table);

/**
 * The type the feed reports for one audited write, e.g. `SHARE_ASSOCIATION_UPDATE`. Shared so
 * the filter can name the same strings the feed emits, rather than re-deriving the convention.
 */
export const dealEventTypeFor = (table: string, action: string): string =>
	`${table.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase()}_${action}`;
